import type {
  CertificationRequestAttributes,
  TopicAttributes,
  UserAttributes,
} from '@/server/models'

import { CertificationRequestType } from '@/queries/constants'
import { SQL, type SQLFragment } from '@/client/query'

import CertificationReviewQ from './CertificationReviewQ'
import {
  type AssociationDefinitions,
  type GroupBy,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryGroupByToSQL,
  queryLimitToSQL,
  queryOrderToSQL,
} from './common'
import TopicQ from './TopicQ'
import UserQ from './UserQ'

// Minimum peer reviews we want each certification request to get
export const MINIMUM_PEER_REVIEWS_PER_CERTIFICATION_REQUEST = 3
// Required peer reviews you must do in order to submit certification request
export const REQUIRED_PEER_REVIEWS_TO_SUBMIT_CERTIFICATION_REQUEST = 2

const CertificationRequestQ = {
  ...makeQueryModelV2<CertificationRequestAttributes>('certification_request')([
    'id',
    'user_id',
    'topic_id',
    'type',
    'reported_score',
    'recording_url',
    'recording_approved_at',
    'recording_approving_user_id',
    'created_at',
    'updated_at',
    'deleted_at',
    'approved_at',
    'rejected_at',
    'reviewer_user_id',
    'attachment_urls',
    'details',
    'review_reason',
    'ready_for_review_at',
  ]),

  associations: (): AssociationDefinitions => ({
    User: {
      query: UserQ,
      foreignKey: UserQ.id,
      sourceKey: CertificationRequestQ.user_id,
    },
    Topic: {
      query: TopicQ,
      foreignKey: TopicQ.id,
      sourceKey: CertificationRequestQ.topic_id,
    },
    Reviewer: {
      query: UserQ,
      foreignKey: UserQ.id,
      sourceKey: CertificationRequestQ.reviewer_user_id,
    },
  }),

  where: (
    {
      reviewed,
      recordingApproved = null,
      readyForReview = null,
      id,
      userId,
      topicId,
      reviewerUserId,
      type,
      include = [],
      groupBy = null,
      order = null,
      limit = null,
      paranoid = true,
    }: {
      reviewed?: 'approved' | 'rejected' | 'unreviewed' | null
      recordingApproved?: boolean | null
      readyForReview?: boolean | null
      id?: number | null | SQLFragment<number>
      userId?: number | null | SQLFragment<number>
      topicId?: number | null | SQLFragment<number>
      reviewerUserId?: number | null | SQLFragment<number>
      type?: CertificationRequestType | null
      include?: IncludeAssociations
      groupBy?: GroupBy | null
      order?: QueryOrder | null
      limit?: number | null
      paranoid?: boolean
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<CertificationRequestAttributes> => SQL`
        FROM ${CertificationRequestQ}
        ${includeAssociationsToSql(CertificationRequestQ, include)}
        WHERE
            ${paranoid ? SQL`${CertificationRequestQ.deleted_at} IS NULL AND` : SQL``}
            ${
              reviewed === 'approved'
                ? SQL`${CertificationRequestQ.approved_at} IS NOT NULL AND`
                : reviewed === 'rejected'
                  ? SQL`${CertificationRequestQ.rejected_at} IS NOT NULL AND`
                  : reviewed === 'unreviewed'
                    ? SQL`(${CertificationRequestQ.approved_at} IS NULL AND ${CertificationRequestQ.rejected_at} IS NULL) AND`
                    : SQL``
            }
            ${
              recordingApproved != null
                ? SQL`${CertificationRequestQ.recording_approved_at} IS ${
                    recordingApproved ? SQL`NOT` : SQL``
                  } NULL AND`
                : SQL``
            }
            ${
              readyForReview != null
                ? SQL`${CertificationRequestQ.ready_for_review_at} IS ${
                    readyForReview ? SQL`NOT` : SQL``
                  } NULL AND`
                : SQL``
            }
            ${id != null ? SQL`${CertificationRequestQ.id} = ${id} AND` : SQL``}
            ${userId != null ? SQL`${CertificationRequestQ.user_id} = ${userId} AND` : SQL``}
            ${
              reviewerUserId != null
                ? SQL`${CertificationRequestQ.reviewer_user_id} = ${reviewerUserId} AND`
                : SQL``
            }
            ${topicId != null ? SQL`${CertificationRequestQ.topic_id} = ${topicId} AND` : SQL``}
            ${type != null ? SQL`${CertificationRequestQ.type} = ${type} AND` : SQL``}
            ${customWhere != null ? customWhere : SQL`TRUE`}
        ${queryGroupByToSQL(groupBy)}
        ${queryOrderToSQL(order)}
        ${queryLimitToSQL(limit)}
      `,

  countReviews: ({
    certificationRequestId,
    flagged = null,
    hasLowScore = null,
    hasUncheckedBox = null,
    hasFeedback = null,
  }: {
    certificationRequestId: number | SQLFragment<number>
    flagged?: boolean | null
    hasLowScore?: boolean | null
    hasUncheckedBox?: boolean | null
    hasFeedback?: boolean | null
  }): SQLFragment<number> =>
    SQL`COUNT(*)::int ${CertificationReviewQ.where(
      {
        certificationRequestId,
        excludeUnreviewed: true,
      },
      SQL`
        ${
          flagged == null
            ? SQL``
            : SQL`${flagged ? SQL`` : SQL`NOT`} (${CertificationReviewQ.isFlagged()}) AND`
        }
        ${
          hasLowScore == null
            ? SQL``
            : SQL`${hasLowScore ? SQL`` : SQL`NOT`} (${CertificationReviewQ.hasLowScore()}) AND`
        }
        ${
          hasUncheckedBox == null
            ? SQL``
            : SQL`${
                hasUncheckedBox ? SQL`` : SQL`NOT`
              } (${CertificationReviewQ.hasUncheckedBox()}) AND`
        }
        ${
          hasFeedback == null
            ? SQL``
            : SQL`${hasFeedback ? SQL`` : SQL`NOT`} (${CertificationReviewQ.hasFeedback()}) AND`
        }
        TRUE
      `
    )}`,

  getTopic: (): SQLFragment<TopicAttributes> =>
    SQL`FROM ${TopicQ} WHERE ${TopicQ.id} = ${CertificationRequestQ.topic_id}`,

  getUser: (): SQLFragment<UserAttributes> =>
    SQL`FROM ${UserQ} WHERE ${UserQ.id} = ${CertificationRequestQ.user_id}`,

  count_certification_request_reviews: SQL<number>`"certification_request__count_reviews"`,

  getCertificationRequestsAvailableForPeerReview: ({
    reviewerUserId,
    limit = null,
  }: {
    reviewerUserId: number
    limit?: number | null
  }): SQLFragment<CertificationRequestAttributes> => SQL`
        FROM (
          SELECT * FROM (
            SELECT ${CertificationRequestQ}.*,
            (SELECT COUNT(*) ${CertificationReviewQ.where({
              certificationRequestId: CertificationRequestQ.id,
              excludeUnreviewed: true,
            })}) as ${CertificationRequestQ.count_certification_request_reviews}
            ${CertificationRequestQ.where(
              {
                reviewed: 'unreviewed',
                recordingApproved: true,
                type: CertificationRequestType.Video,
                include: [{ association: 'User', required: true }],
              },
              SQL`${CertificationRequestQ.user_id} != ${reviewerUserId}
              AND NOT EXISTS (SELECT 1 ${CertificationReviewQ.where({
                certificationRequestId: CertificationRequestQ.id,
                reviewerUserId,
              })})
              AND ${UserQ.hasBeenApproved()}`
            )}
          ) as ${CertificationRequestQ}
          WHERE ${CertificationRequestQ.count_certification_request_reviews}
            < ${MINIMUM_PEER_REVIEWS_PER_CERTIFICATION_REQUEST}
          ORDER BY ${CertificationRequestQ.created_at} ASC
          ${queryLimitToSQL(limit)}
        ) as x`,

  getCertificationRequestToPeerReview: (
    reviewerUserId: number
  ): SQLFragment<CertificationRequestAttributes> => SQL`
        ${CertificationRequestQ.getCertificationRequestsAvailableForPeerReview({
          reviewerUserId,
          limit: 10,
        })} ORDER BY RANDOM() LIMIT 1`,

  countCertificationRequestsToPeerReview: (reviewerUserId: number): SQLFragment<number> => SQL`
        COUNT(*) ${CertificationRequestQ.getCertificationRequestsAvailableForPeerReview({
          reviewerUserId,
        })}`,

  getReviewTime: (): SQLFragment<string | null> => SQL`
    CASE
        WHEN ${CertificationRequestQ.ready_for_review_at} IS NOT NULL
          AND ${CertificationRequestQ.approved_at} IS NOT NULL THEN
          ${CertificationRequestQ.approved_at} - ${CertificationRequestQ.ready_for_review_at}
        WHEN ${CertificationRequestQ.ready_for_review_at} IS NOT NULL
          AND ${CertificationRequestQ.rejected_at} IS NOT NULL THEN
          ${CertificationRequestQ.rejected_at} - ${CertificationRequestQ.ready_for_review_at}
        ELSE INTERVAL '100 DAYS'
      END`,
}

export default CertificationRequestQ
